import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { GOLD, fmt, num, Field, Card, Row } from './shared';

export const BurdenRateTab = ({ api, readOnly }) => {
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/accounting/burden-rate').then(r => { setResult(r.data); setSettings(r.data.settings); }).catch(() => {});
  useEffect(() => { load(); }, [api]);
  const upd = (k, v) => setSettings({ ...settings, [k]: v });
  const save = async () => {
    setSaving(true);
    try { const r = await api.post('/accounting/burden-rate/settings', settings); setResult(r.data); setSettings(r.data.settings); toast.success('Burden rate recalculated'); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  if (!result || !settings) return <p className="text-gray-500">Loading...</p>;
  const c = result.calculations;
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="fos-burden-rate-tab">
      <Card className="lg:col-span-2">
        <h3 className="font-semibold text-gray-900 mb-4">Inputs</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Owner Salary 1" value={settings.owner_salary_1} onChange={(v) => upd('owner_salary_1', v)} readOnly={readOnly} testid="br-owner1" />
          <Field label="Owner Salary 2" value={settings.owner_salary_2} onChange={(v) => upd('owner_salary_2', v)} readOnly={readOnly} testid="br-owner2" />
          <Field label="Employee Wages" value={settings.employee_wages} onChange={(v) => upd('employee_wages', v)} readOnly={readOnly} testid="br-wages" />
          <Field label="Payroll Tax %" value={settings.payroll_tax_rate} onChange={(v) => upd('payroll_tax_rate', v)} readOnly={readOnly} testid="br-payroll" suffix="%" />
          <Field label="Workers Comp %" value={settings.workers_comp_rate} onChange={(v) => upd('workers_comp_rate', v)} readOnly={readOnly} testid="br-wc" suffix="%" />
          <Field label="Field Workers" value={settings.field_workers} onChange={(v) => upd('field_workers', v)} readOnly={readOnly} testid="br-workers" />
          <Field label="Working Weeks" value={settings.working_weeks} onChange={(v) => upd('working_weeks', v)} readOnly={readOnly} testid="br-weeks" />
          <Field label="Hours / Week" value={settings.hours_per_week} onChange={(v) => upd('hours_per_week', v)} readOnly={readOnly} testid="br-hours" />
          <Field label="Efficiency (billable)" value={settings.efficiency_factor} onChange={(v) => upd('efficiency_factor', v)} readOnly={readOnly} testid="br-efficiency" />
          <Field label="Target Margin %" value={settings.target_profit_margin} onChange={(v) => upd('target_profit_margin', v)} readOnly={readOnly} testid="br-target" suffix="%" />
        </div>
        {!readOnly && (
          <button data-testid="br-save-btn" onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#C6A87C] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Calculating...' : 'Recalculate & Save'}
          </button>
        )}
      </Card>
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Calculated Rates</h3>
        <div className="space-y-3 text-sm">
          <Row label="Total Annual Cost" value={fmt(c.total_annual_cost)} />
          <Row label="Productive Hours" value={num(Math.round(c.productive_hours?.productive_hours))} />
          <div className="pt-3 border-t border-gray-200 space-y-3">
            <Row label="Break-Even Rate" value={`${fmt(c.rates?.break_even_rate)}/hr`} testid="br-breakeven" />
            <Row label="Minimum Sell Rate" value={`${fmt(c.rates?.minimum_sell_rate)}/hr`} />
            <Row label="Standard Sell Rate" value={`${fmt(c.rates?.standard_sell_rate)}/hr`} bold accent={GOLD} testid="br-standard" />
            <Row label="High-Risk Rate" value={`${fmt(c.rates?.premium_sell_rate)}/hr`} />
          </div>
        </div>
      </Card>
    </div>
  );
};
